// Free-edit helpers for the announcement paper (used by FormWrapper's "แก้ไขข้อความ / วาระ" mode).
// They work on a plain DOM copy of the paper, so they apply to every form template without
// changing the templates themselves.

// Agenda rows in the templates: Thai `.inv-agenda-item`, English `.en-ag-row` / `.en-agenda-item`
const AGENDA_SELECTOR = ".inv-agenda-item, .en-ag-row, .en-agenda-item";
const NUMBER_RE = /(วาระที่\s*|Agenda\s*)(\d+)/i;

export function agendaBlocks(root) {
  if (!root) return [];
  const marked = Array.from(root.querySelectorAll(AGENDA_SELECTOR));
  if (marked.length) return marked;
  // templates without agenda classes: any small block whose text starts with "วาระที่ N" / "Agenda N"
  return Array.from(root.querySelectorAll("p, div, li")).filter(
    (el) => el.children.length <= 3 && /^\s*(วาระที่|Agenda)\s*\d+/i.test(el.textContent || "")
  );
}

// Replace the first "วาระที่ N" / "Agenda N" inside an element (searching text nodes only)
function setNumber(el, n) {
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();
  while (node) {
    if (NUMBER_RE.test(node.nodeValue)) {
      node.nodeValue = node.nodeValue.replace(NUMBER_RE, (m, label) => `${label}${n}`);
      return true;
    }
    node = walker.nextNode();
  }
  return false;
}

export function renumberAgendas(root) {
  let n = 1;
  for (const block of agendaBlocks(root)) {
    if (setNumber(block, n)) n += 1;
  }
}

// Sub-lines that belong to the agenda above them (e.g. "ณ วันที่ ...")
function lastLineOfAgenda(block) {
  let end = block;
  while (end.nextElementSibling && end.nextElementSibling.classList.contains("inv-agenda-sub")) {
    end = end.nextElementSibling;
  }
  return end;
}

function selectContents(el) {
  const range = document.createRange();
  range.selectNodeContents(el);
  const sel = window.getSelection();
  sel.removeAllRanges();
  sel.addRange(range);
  el.focus?.();
}

// Add a new agenda after the last one; returns false when the template has no agenda list
export function addAgenda(root) {
  const blocks = agendaBlocks(root);
  if (blocks.length === 0) return false;
  const last = blocks[blocks.length - 1];
  const english = /Agenda/i.test(last.textContent || "");
  const next = blocks.length + 1;

  let el;
  let textTarget;
  if (last.classList.contains("en-ag-row")) {
    el = document.createElement("div");
    el.className = last.className;
    const left = document.createElement("div");
    left.className = last.querySelector(".en-ag-left")?.className || "en-ag-left en-bold";
    left.textContent = `Agenda ${next}`;
    const right = document.createElement("div");
    right.className = last.querySelector(".en-ag-right")?.className || "en-ag-right";
    right.textContent = "To consider (type the agenda here)";
    el.append(left, right);
    textTarget = right;
  } else {
    el = document.createElement(last.tagName.toLowerCase());
    el.className = last.className;
    el.textContent = english ? `Agenda ${next} To consider (type the agenda here)` : `วาระที่ ${next} พิจารณา (พิมพ์รายละเอียดวาระ)`;
    textTarget = el;
  }

  lastLineOfAgenda(last).after(el);
  renumberAgendas(root);
  selectContents(textTarget);
  return true;
}

// The editable block that holds the caret (an agenda row, a paragraph, a line …)
export function blockAtSelection(root) {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return null;
  let node = sel.anchorNode;
  if (node && node.nodeType === Node.TEXT_NODE) node = node.parentElement;
  if (!node || !root.contains(node)) return null;
  const block = node.closest(`${AGENDA_SELECTOR}, .inv-agenda-sub, p, li, tr, h1, h2, h3, h4, div`);
  if (!block || block === root || !root.contains(block) || block === root.firstElementChild) return null;
  // never treat a whole section (a wrapper holding many lines) as "one line"
  if (!block.matches(AGENDA_SELECTOR) && block.querySelectorAll("p, div, li, tr").length > 3) return null;
  return block;
}

export function removeBlockAtSelection(root) {
  const block = blockAtSelection(root);
  if (!block) return false;
  const wasAgenda = block.matches(AGENDA_SELECTOR);
  // an agenda takes its sub-lines with it
  if (wasAgenda) {
    let sub = block.nextElementSibling;
    while (sub && sub.classList.contains("inv-agenda-sub")) {
      const nextSub = sub.nextElementSibling;
      sub.remove();
      sub = nextSub;
    }
  }
  block.remove();
  if (wasAgenda) renumberAgendas(root);
  return true;
}
